import { memo, useState } from 'react';
import { ChevronRight, ChevronDown, Copy, Check, ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { SubAgentGroup } from './useEventGrouping';
import { getLatestNonDeltaEvent } from './useEventGrouping';
import { getEventPreviewText } from './event-renderers';
import EventCard from './EventCard';

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const statusConfig = {
  completed: { label: 'completed', variant: 'success' as const },
  failed: { label: 'failed', variant: 'error' as const },
  running: { label: 'running', variant: 'warning' as const },
  cancelled: { label: 'cancelled', variant: 'warning' as const },
};

function SubAgentGroupCardInner({ group }: { group: SubAgentGroup }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEvents, setCopiedEvents] = useState(false);
  const config = statusConfig[group.status];
  const previewEvent = getLatestNonDeltaEvent(group.events);

  return (
    <div data-testid="subagent-group-card" className="rounded-lg border border-l-2 border-l-accent-subagent-group-border/40 bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-2.5 flex items-center gap-2.5 hover:bg-accent/50 transition-colors text-left rounded-t-lg"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
          {formatTimestamp(group.latestTimestamp)}
        </span>
        {group.agentName && (
          <Badge variant="purple">{group.agentName}</Badge>
        )}
        <span className="text-xs text-muted-foreground truncate">
          Run: {group.runId}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(group.runId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          title="Copy run ID"
        >
          {copied ? (
            <Check className="h-3 w-3" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
        <Badge variant={config.variant}>
          {group.status === 'running' && (
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-yellow-500 animate-pulse mr-1" />
          )}
          {config.label}
        </Badge>
        <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const payload = {
                runId: group.runId,
                agentId: group.events[0]?.data?.agentId,
                agentName: group.agentName,
                status: group.status,
                eventCount: group.events.length,
                firstEventTimestamp: group.events[group.events.length - 1]?.metadata.timestamp,
                lastEventTimestamp: group.events[0]?.metadata.timestamp,
                events: group.events,
              };
              navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
              setCopiedEvents(true);
              setTimeout(() => setCopiedEvents(false), 1500);
            }}
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title="Copy all sub-agent events"
          >
            {copiedEvents ? (
              <Check className="h-3 w-3" />
            ) : (
              <ClipboardList className="h-3 w-3" />
            )}
          </button>
          {group.events.length} event{group.events.length !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Collapsed preview */}
      {!expanded && previewEvent && (
        <p className="px-2.5 pb-2 ml-7 text-xs text-muted-foreground truncate">
          {getEventPreviewText(previewEvent)}
        </p>
      )}

      {/* Expanded: newest-first event list */}
      {expanded && (
        <div className="ml-4 mt-4 mb-1 pb-3 pl-2 space-y-2">
          {group.events.map((event) => (
            <EventCard key={event.eventId} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

const SubAgentGroupCard = memo(SubAgentGroupCardInner);
export default SubAgentGroupCard;
